<mable-heading
  [ngClass]="{ invalid: hasSubmitted && rateCount === 0 }"
  heading="h2"
  headingStyle="h3"
  >Rates</mable-heading
>
<p class="ion-margin-horizontal">Agreed rates include {{ carerFeePercentage }}% worker service fee.</p>

<!-- Indicative rates banner -->
<div
  class="prefilled-banner"
  *ngIf="shouldShowIndicativeRatesBanner"
>
  <mable-banner category="callout">
    <ng-container class="content">
      <mable-heading
        heading="h3"
        headingStyle="h5"
        >We’ve added your indicative rates</mable-heading
      >
      <p class="ion-no-margin">
        If required, change the rates below to the actual rates you’ve agreed with your client.
      </p>
    </ng-container>
  </mable-banner>
</div>

<!-- Service categories applied banner -->
<app-service-categories-applied-banner
  *ngIf="serviceCategoriesAppliedBannerContext.shouldShowServiceCategoriesAvailableBanner"
  [clientFirstName]="serviceCategoriesAppliedBannerContext.clientFirstName"
  [availableBudgetSupportTypes]="availableBudgetSupportTypes"
  [defaultBudgetSupportTypes]="defaultBudgetSupportTypes"
  [isServicesAllocated]="serviceCategoriesAppliedBannerContext.hasSupportTypesBeenAllocatedToExistingAgreement"
  [isSpecialisedServicesAvailable]="serviceCategoriesAppliedBannerContext.hasSpecialisedSupportItemBeenAdded"
  [specialisedBudgetSupportTypes]="specialisedBudgetSupportTypes"
  [consumerUuid]="serviceCategoriesAppliedBannerContext.consumerUuid"
  [supportProviderUuid]="serviceCategoriesAppliedBannerContext.supportProviderUuid"
></app-service-categories-applied-banner>
<hr />

<!-- Rates form -->
<div
  *ngIf="ratesForm && rateCount > 0"
  [formGroup]="ratesForm"
>
  <ng-container formArrayName="rates">
    <ion-list>
      <div
        *ngFor="let rate of rates.controls; index as control"
        [formGroupName]="control"
        class="rates"
        [class.missing]="!rate.controls.rateName.valid"
        [hidden]="rate.controls.destroy.value"
      >
        <div class="rates__info-container">
          <ion-item
            class="default listItem"
            lines="none"
          >
            <div class="rates__details">
              <ion-label class="ion-text-wrap">
                <!-- Rate name -->
                <mable-heading
                  heading="h4"
                  headingStyle="h3"
                  class="rates__name"
                >
                  {{ rate.controls.rateName.value }}
                </mable-heading>

                <!-- Rate description -->
                <p
                  class="rates__description form-description-16 no-margin half-margin-vertical"
                  *ngIf="rate.controls.rateDescription.value"
                >
                  <span class="screen-reader-only">Rate description</span>
                  {{ rate.controls.rateDescription.value }}
                </p>

                <!-- Rate budget support types -->
                <app-support-service-types
                  *ngIf="!rate.value.flatRate && shouldShowBudgetSupportTypeInformation"
                  [budgetSupportTypes]="rate.value.budgetSupportTypes"
                ></app-support-service-types>

                <!-- Rate schedule -->
                <app-rate-schedule
                  *ngIf="!rate.value.flatRate"
                  [rateSchedule]="rate.value.rateSchedule"
                ></app-rate-schedule>
              </ion-label>

              <!-- TODO (ES-30816): Update the data-testid once implemented in new streamline flow -->
              <mable-button
                class="rates__edit"
                icon="pencil"
                category="header-detailed"
                (onClick)="editRate(rate)"
                data-testid="agreement-form-step3-edit-button"
              >
                Edit
              </mable-button>
            </div>
          </ion-item>

          <!-- Rate Input -->
          <div class="ion-margin-top">
            <mable-input
              [label]="rate.controls.flatRate.value ? 'Agreed flat rate' : 'Agreed hourly rate'"
              icon="fa-dollar-sign-solid"
              class="rates__input has-info"
              [hasError]="rate.controls.rateCents.showError()"
            >
              <ion-input
                type="text"
                formControlName="rateCents"
                [min]="rate.controls.flatRate.value ? 1 : minimumRate"
                max="9999.99"
                step="1"
                #rateInput
                data-testid="agreement-form-step3-rate-cents"
                [attr.aria-label]="rate.controls.flatRate.value ? 'Agreed flat rate' : 'Agreed hourly rate'"
              ></ion-input>

              <!-- Rate validation errors -->
              <div
                *ngIf="rate.controls.rateCents.errors as rateErrors"
                class="warn"
              >
                <p *ngIf="rateErrors.required">Enter an amount for this rate.</p>
                <p *ngIf="rateErrors.pattern; else minMaxErrors">Provide a numeric value</p>
                <ng-template #minMaxErrors>
                  <p *ngIf="rateErrors.max">Amount must be less than $10,000.</p>
                  <p *ngIf="rateErrors.min">
                    <span *ngIf="rate.controls.flatRate.value; else minHourlyRateError"
                      >Please enter a value greater than 0</span
                    >
                  </p>
                </ng-template>
                <ng-template #minHourlyRateError>
                  <span> Offer a minimum rate of ${{ minimumRate }} for active support under our terms of use. </span>
                </ng-template>
              </div>

              <!-- Net rate info -->
              <p
                class="info"
                *ngIf="!rate.controls.rateCents.showError()"
              >
                Net rate you will be paid after fees is ${{ rate.value.rateWithFee }}
              </p>
            </mable-input>

            <!-- Price Limit Alert Banner -->
            <ng-container *ngIf="rate?.value?.showCapRateBanner">
              <app-ndis-price-limit-alert
                [priceLimitAlertData]="this.priceLimitAlertData"
                [rateData]="rate.value"
                (editRate)="editRate(rate)"
              >
              </app-ndis-price-limit-alert>
            </ng-container>

            <mable-button
              icon="trash"
              category="header-detailed"
              (onClick)="removeRate(control)"
            >
              Delete rate
            </mable-button>
          </div>
        </div>
        <hr />
      </div>
    </ion-list>
  </ng-container>
</div>

<!-- Add rate -->
<div class="ion-margin-horizontal">
  <mable-button
    category="outline"
    icon="add"
    expand="block"
    (onClick)="addRate()"
    data-testid="agreement-form-step3-add-rate-button"
  >
    {{ rateCount > 0 ? 'Add another rate' : 'Add rate' }}
  </mable-button>
  <div
    class="error-message"
    *ngIf="hasSubmitted && rateCount === 0"
  >
    <ion-icon
      slot="start"
      name="alert-circle"
    ></ion-icon>
    <p class="warn">Add a rate. All agreements must include at least one rate.</p>
  </div>
</div>
